import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail, Plus, Pencil, Trash2, Send, ToggleLeft, ToggleRight, Loader2,
  ChevronDown, X, Clock, Calendar, Users, Info, Search, Eye, EyeOff,
  FileText, BarChart3, AlertTriangle, Bot, GitBranch, RefreshCw,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getAiInsightEmailSettings,
  createAiInsightEmailSetting,
  updateAiInsightEmailSetting,
  deleteAiInsightEmailSetting,
  toggleAiInsightEmailSetting,
  sendTestAiInsightEmail,
} from '../../api/adminApi';
import { getActiveTeamLookups } from '../../api/teamApi';
import { AdminInsightEmailsSkeleton } from '../../components/common/Skeleton';
import { PageFadeSection } from '../../components/common/PageFadeSection';

const FREQUENCIES = ['Weekly', 'BiWeekly', 'Monthly'];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const HOURS = [6, 7, 8, 9, 10, 11, 12, 14, 16, 18];

const EMPTY_FORM = {
  name: '', recipients: '', teamFilter: '', frequency: 'Weekly', dayOfWeek: 1, hourOfDay: 8, isEnabled: true,
  showTimeLogged: true, showWeeklyHours: true, showTasksCompleted: true, showTasksOngoing: true,
  showOverdueTasks: true, showAiRemarks: true, showTeamBreakdown: true, showSyncStatus: false,
  customSubject: '', customIntroMessage: '',
};

const AiInsightEmailsPage = () => {
  const [settings, setSettings] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [recipientInput, setRecipientInput] = useState('');
  const [sendingTest, setSendingTest] = useState(null);
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);

  const load = useCallback(async () => {
    try {
      const [settingsRes, teamsRes] = await Promise.all([
        getAiInsightEmailSettings(),
        getActiveTeamLookups(),
      ]);
      setSettings(Array.isArray(settingsRes) ? settingsRes : settingsRes?.data ?? []);
      setTeams(Array.isArray(teamsRes) ? teamsRes : teamsRes?.data ?? []);
    } catch {
      toast.error('Failed to load AI insight email settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const recipientsList = form.recipients ? form.recipients.split(',').map(e => e.trim()).filter(Boolean) : [];
  const selectedTeamIds = form.teamFilter ? form.teamFilter.split(',').filter(Boolean) : [];

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY_FORM }); setRecipientInput(''); setShowEditor(true); };
  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name, recipients: item.recipients, teamFilter: item.teamFilter || '',
      frequency: item.frequency || 'Weekly', dayOfWeek: item.dayOfWeek ?? 1, hourOfDay: item.hourOfDay ?? 8, isEnabled: item.isEnabled,
      showTimeLogged: item.showTimeLogged ?? true, showWeeklyHours: item.showWeeklyHours ?? true,
      showTasksCompleted: item.showTasksCompleted ?? true, showTasksOngoing: item.showTasksOngoing ?? true,
      showOverdueTasks: item.showOverdueTasks ?? true, showAiRemarks: item.showAiRemarks ?? true,
      showTeamBreakdown: item.showTeamBreakdown ?? true, showSyncStatus: item.showSyncStatus ?? false,
      customSubject: item.customSubject || '', customIntroMessage: item.customIntroMessage || '',
    });
    setRecipientInput('');
    setShowEditor(true);
  };

  const addRecipient = () => {
    const email = recipientInput.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error('Enter a valid email'); return; }
    if (recipientsList.includes(email)) { toast.error('Already added'); return; }
    setForm(p => ({ ...p, recipients: [...recipientsList, email].join(', ') }));
    setRecipientInput('');
  };

  const removeRecipient = (email) => {
    setForm(p => ({ ...p, recipients: recipientsList.filter(e => e !== email).join(', ') }));
  };

  const toggleTeam = (teamId) => {
    const list = [...selectedTeamIds];
    const idx = list.indexOf(teamId);
    if (idx >= 0) list.splice(idx, 1); else list.push(teamId);
    setForm(p => ({ ...p, teamFilter: list.join(',') }));
  };

  const getTeamNames = (filter) => {
    if (!filter) return 'All Teams';
    const ids = filter.split(',').filter(Boolean);
    if (ids.length === 0) return 'All Teams';
    return ids.map(id => teams.find(t => (t.teamId || t.id) === id)?.teamName || id).join(', ');
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.recipients.trim()) { toast.error('Add at least one recipient'); return; }
    setSaving(true);
    try {
      if (editing) {
        const res = await updateAiInsightEmailSetting(editing.id, form);
        const updated = res?.data ?? res;
        setSettings(prev => prev.map(s => s.id === editing.id ? updated : s));
        toast.success('Rule updated');
      } else {
        const res = await createAiInsightEmailSetting(form);
        const created = res?.data ?? res;
        setSettings(prev => [created, ...prev]);
        toast.success('Rule created');
      }
      setShowEditor(false);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this rule? This cannot be undone.')) return;
    try {
      await deleteAiInsightEmailSetting(id);
      setSettings(prev => prev.filter(s => s.id !== id));
      toast.success('Rule deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleToggle = async (id) => {
    try {
      const res = await toggleAiInsightEmailSetting(id);
      const updated = res?.data ?? res;
      setSettings(prev => prev.map(s => s.id === id ? updated : s));
    } catch { toast.error('Failed to toggle'); }
  };

  const handleSendTest = async (id) => {
    setSendingTest(id);
    try {
      const res = await sendTestAiInsightEmail(id);
      toast.success(res?.message || 'Test email sent');
    } catch { toast.error('Failed to send test'); }
    finally { setSendingTest(null); }
  };

  if (loading) {
    return <AdminInsightEmailsSkeleton />;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <PageFadeSection>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center">
            <Mail className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">AI Insight Emails</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Configure automated AI-powered insight emails for stakeholders</p>
          </div>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition font-medium text-sm"
        >
          <Plus className="w-4 h-4" /> New Rule
        </button>
      </div>

      {/* Rules list */}
      {settings.length === 0 ? (
        <div className="text-center py-20">
          <Mail className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <p className="text-gray-500 dark:text-gray-400 font-medium">No rules configured</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Create a rule to start sending automated AI insight emails</p>
        </div>
      ) : (
        <div className="space-y-4">
          {settings.map((item) => {
            const recCount = item.recipients ? item.recipients.split(',').filter(Boolean).length : 0;
            return (
              <div key={item.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-5 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${item.isEnabled ? 'bg-indigo-50 dark:bg-indigo-500/10' : 'bg-gray-100 dark:bg-gray-700'}`}>
                      <Mail className={`w-5 h-5 ${item.isEnabled ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400'}`} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-white truncate">{item.name || 'Untitled'}</h3>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" />{recCount} recipient{recCount !== 1 ? 's' : ''}</span>
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{item.frequency}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{DAYS[item.dayOfWeek]} {item.hourOfDay}:00</span>
                      </div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 truncate">{getTeamNames(item.teamFilter)}</p>
                      {item.lastSentAt && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Last sent: {new Date(item.lastSentAt).toLocaleDateString()}</p>
                      )}
                    </div>
                  </div>
                  <button onClick={() => handleToggle(item.id)} title={item.isEnabled ? 'Disable' : 'Enable'}>
                    {item.isEnabled
                      ? <ToggleRight className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
                      : <ToggleLeft className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                    }
                  </button>
                </div>

                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                  <button onClick={() => openEdit(item)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 rounded-lg transition">
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button onClick={() => handleSendTest(item.id)} disabled={sendingTest === item.id} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-500/10 rounded-lg transition">
                    {sendingTest === item.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Test
                  </button>
                  <button onClick={() => handleDelete(item.id)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition">
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 pt-6 pb-2">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">{editing ? 'Edit Rule' : 'Create Rule'}</h2>
              <button onClick={() => setShowEditor(false)} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 pb-6 space-y-5">
              {/* Name */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Rule Name</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                  placeholder="e.g. Weekly Executive Report"
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                />
              </div>

              {/* Recipients */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Recipients</label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    className="flex-1 px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                    placeholder="email@example.com"
                    value={recipientInput}
                    onChange={e => setRecipientInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addRecipient())}
                  />
                  <button onClick={addRecipient} className="px-3 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2 min-h-[28px]">
                  {recipientsList.length === 0 && <span className="text-xs text-gray-400 italic">No recipients added</span>}
                  {recipientsList.map(email => (
                    <span key={email} className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium">
                      {email}
                      <button onClick={() => removeRecipient(email)} className="hover:text-indigo-900 dark:hover:text-indigo-100">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Team filter */}
              <div className="relative">
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Team Filter</label>
                <button
                  onClick={() => setShowTeamDropdown(!showTeamDropdown)}
                  className="w-full flex items-center justify-between px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-sm text-left"
                >
                  <span className={`truncate ${selectedTeamIds.length > 0 ? 'text-gray-900 dark:text-white' : 'text-gray-400'}`}>
                    {getTeamNames(form.teamFilter)}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                </button>
                {showTeamDropdown && (
                  <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                    <button
                      onClick={() => { setForm(p => ({ ...p, teamFilter: '' })); setShowTeamDropdown(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200"
                    >
                      <span className={`w-4 h-4 rounded border flex items-center justify-center text-xs ${!form.teamFilter ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-gray-300 dark:border-gray-500'}`}>
                        {!form.teamFilter && '✓'}
                      </span>
                      All Teams
                    </button>
                    {teams.map(t => {
                      const tid = t.teamId || t.id;
                      const checked = selectedTeamIds.includes(tid);
                      return (
                        <button
                          key={tid}
                          onClick={() => toggleTeam(tid)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200"
                        >
                          <span className={`w-4 h-4 rounded border flex items-center justify-center text-xs ${checked ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-gray-300 dark:border-gray-500'}`}>
                            {checked && '✓'}
                          </span>
                          {t.teamName || t.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Frequency */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Frequency</label>
                <div className="flex gap-2">
                  {FREQUENCIES.map(f => (
                    <button
                      key={f}
                      onClick={() => setForm(p => ({ ...p, frequency: f }))}
                      className={`px-4 py-2 rounded-xl text-sm font-medium border transition ${form.frequency === f
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </div>

              {/* Day + Hour */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Day of Week</label>
                  <select
                    value={form.dayOfWeek}
                    onChange={e => setForm(p => ({ ...p, dayOfWeek: Number(e.target.value) }))}
                    className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                  >
                    {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Send Time</label>
                  <select
                    value={form.hourOfDay}
                    onChange={e => setForm(p => ({ ...p, hourOfDay: Number(e.target.value) }))}
                    className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                  >
                    {HOURS.map(h => <option key={h} value={h}>{h}:00</option>)}
                  </select>
                </div>
              </div>

              {/* Enabled toggle */}
              <div className="flex items-center justify-between py-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Active</span>
                <button onClick={() => setForm(p => ({ ...p, isEnabled: !p.isEnabled }))}>
                  {form.isEnabled
                    ? <ToggleRight className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
                    : <ToggleLeft className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                  }
                </button>
              </div>

              {/* ─── Email Template Configuration ─── */}
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" /> Email Template Sections
                </h3>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">Choose which sections appear in the AI insight email</p>

                <div className="space-y-2.5">
                  {[
                    { key: 'showTimeLogged', label: 'Hours Logged', desc: 'Total time tracked', icon: Clock },
                    { key: 'showWeeklyHours', label: 'Performance Summary', desc: 'Members, teams, completion rate', icon: BarChart3 },
                    { key: 'showTasksCompleted', label: 'Tasks Completed', desc: 'Completed task count card', icon: Eye },
                    { key: 'showTasksOngoing', label: 'Tasks In Progress', desc: 'Active tasks count card', icon: Eye },
                    { key: 'showOverdueTasks', label: 'Overdue Tasks', desc: 'Overdue tasks with details', icon: AlertTriangle },
                    { key: 'showTeamBreakdown', label: 'Team Breakdown', desc: 'Per-team stats table', icon: Users },
                    { key: 'showAiRemarks', label: 'AI Remarks', desc: 'AI-generated performance insights', icon: Bot },
                    { key: 'showSyncStatus', label: 'SAP Sync Status', desc: 'Synced/Pending/Failed counts', icon: RefreshCw },
                  ].map(({ key, label, desc, icon: Icon }) => (
                    <div key={key} className="flex items-center justify-between py-1.5">
                      <div className="flex items-center gap-2.5">
                        <Icon className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{label}</p>
                          <p className="text-xs text-gray-400 dark:text-gray-500">{desc}</p>
                        </div>
                      </div>
                      <button onClick={() => setForm(p => ({ ...p, [key]: !p[key] }))}>
                        {form[key]
                          ? <ToggleRight className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
                          : <ToggleLeft className="w-7 h-7 text-gray-300 dark:text-gray-600" />
                        }
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Custom Subject & Intro */}
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Customization</h3>
                <div className="mb-3">
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Custom Subject Line</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                    placeholder="Leave empty for auto-generated subject"
                    value={form.customSubject}
                    onChange={e => setForm(p => ({ ...p, customSubject: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Intro Message</label>
                  <textarea
                    className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none resize-none"
                    rows={3}
                    placeholder="Optional intro shown at top of the email"
                    value={form.customIntroMessage}
                    onChange={e => setForm(p => ({ ...p, customIntroMessage: e.target.value }))}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowEditor(false)}
                  className="flex-1 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editing ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </PageFadeSection>
    </div>
  );
};

export default AiInsightEmailsPage;
